import React, { useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, LinearProgress, TablePagination,
  Avatar, Select,
} from '@mui/material';
import { Add, Delete, Visibility, CheckBoxOutlineBlank, ArrowBack, Refresh } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';
import { useAuth } from '../../context/AuthContext';

const CAT_COLORS = [
  '#e8eaf6', '#e8f5e9', '#fff3e0', '#e3f2fd',
  '#fce4ec', '#f3e5f5', '#e0f7fa', '#fff8e1',
];
const CAT_ICONS = ['📚', '🔢', '🤝', '✏️', '👟', '🧠', '🎨', '🌍'];

export default function LearningModules() {
  const { user } = useAuth();
  const [view, setView] = useState('grid');
  const [selected, setSelected] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Create dialog
  const [dialog, setDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', description: '' });
  const [categories, setCategories] = useState([
    { title: '', icon: '📚', color: CAT_COLORS[0], items: [''] }
  ]);

  // View submissions dialog
  const [subDialog, setSubDialog] = useState(false);
  const [subModule, setSubModule] = useState(null);
  const [subData, setSubData] = useState(null);
  const [subLoading, setSubLoading] = useState(false);

  // ── Load modules ─────────────────────────────────────────────────────────────
  const load = useCallback(async (cls) => {
    if (!cls) return;
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getLearningModules, {
      standard: cls.standard, section: cls.section, academic_year: cls.acYear,
    });
    setModules(data?.modules || []);
    setLoading(false);
  }, []);

  const handleSelect = (cls) => {
    setSelected(cls);
    setView('list');
    setModules([]);
    load(cls);
  };

  // ── Create ────────────────────────────────────────────────────────────────────
  const addCategory = () =>
    setCategories(p => [...p, { title: '', icon: CAT_ICONS[p.length % CAT_ICONS.length], color: CAT_COLORS[p.length % CAT_COLORS.length], items: [''] }]);

  const removeCategory = (ci) => setCategories(p => p.filter((_, i) => i !== ci));
  const setCatField = (ci, field, val) => setCategories(p => p.map((c, i) => i === ci ? { ...c, [field]: val } : c));
  const addItem = (ci) => setCategories(p => p.map((c, i) => i === ci ? { ...c, items: [...c.items, ''] } : c));
  const removeItem = (ci, ii) => setCategories(p => p.map((c, i) => i === ci ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c));
  const setItem = (ci, ii, v) => setCategories(p => p.map((c, i) => i === ci ? { ...c, items: c.items.map((it, j) => j === ii ? v : it) } : c));

  const handleSave = async () => {
    if (!form.title.trim() || !selected) return;
    setSaving(true);
    const res = await InvokeApi(Config.main_url + apiList.addLearningModule, {
      title: form.title.trim(),
      description: form.description.trim(),
      standard: selected.standard,
      section: selected.section,
      academic_year: selected.acYear,
      created_by: parseInt(user?.adminId || 1),
      categories: categories.filter(c => c.title.trim()).map(c => ({
        title: c.title.trim(), icon: c.icon, color: c.color,
        items: c.items.filter(it => it.trim()),
      })),
    });
    setSaving(false);
    if (res?.code === '200') {
      setDialog(false);
      setForm({ title: '', description: '' });
      setCategories([{ title: '', icon: '📚', color: CAT_COLORS[0], items: [''] }]);
      load(selected);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this Learning Progress?')) return;
    await InvokeApi(Config.main_url + apiList.deleteLearningModule, { module_id: id });
    load(selected);
  };

  // ── View submissions ──────────────────────────────────────────────────────────
  const openSubmissions = async (mod) => {
    setSubModule(mod);
    setSubDialog(true);
    setSubLoading(true);
    setSubData(null);
    const [detailRes, subRes] = await Promise.all([
      InvokeApi(Config.main_url + apiList.getLearningModuleDetail, { module_id: mod.id }),
      InvokeApi(Config.main_url + apiList.getLearningModuleSubmissions, { module_id: mod.id }),
    ]);
    const itemMap = {};
    (detailRes?.categories || []).forEach(c => c.items.forEach(it => { itemMap[it.id] = it.item_text; }));
    const totalItems = Object.keys(itemMap).length;
    setSubData({ itemMap, totalItems, submissions: subRes?.submissions || [] });
    setSubLoading(false);
  };

  // ── Grid view ─────────────────────────────────────────────────────────────────
  if (view === 'grid') {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Learning Progress</Typography>
          <Typography variant="body2" color="text.secondary">Select a class to manage Learning Progress</Typography>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
          <ClassSectionGrid onSelect={handleSelect} />
        </Card>
      </Box>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────────
  const paged = modules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {/* ── Toolbar ── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={900} color="#1a237e">
            Learning Progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selected?.standard} - {selected?.section} · {selected?.acYear} · {modules.length} Learning Progress
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setView('grid')}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Back</Button>
          <Tooltip title="Refresh">
            <IconButton onClick={() => load(selected)} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}
            onClick={() => setDialog(true)}>
            New Module
          </Button>
        </Box>
      </Box>

      {/* ── Table ── */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#1a237e' }} />
            </Box>
          ) : modules.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: '#bbb' }}>
              <Typography variant="h6">No Learning Progress yet</Typography>
              <Button variant="outlined" startIcon={<Add />} sx={{ mt: 2, borderRadius: 2 }}
                onClick={() => setDialog(true)}>
                Create First Module
              </Button>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13  }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13  }}>Module Title</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13  }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13  }}>Submitted</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13  }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13  }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((mod, idx) => (
                  <TableRow key={mod.id} hover>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" fontWeight={700}>
                        {page * rowsPerPage + idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color="#1a237e">
                        {mod.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {mod.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={`${mod.submission_count} submitted`} size="small"
                        sx={{ background: mod.submission_count > 0 ? '#e8f5e9' : '#f5f5f5', color: mod.submission_count > 0 ? '#2e7d32' : '#888', fontWeight: 700, fontSize: 11 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {mod.created_at?.split(' ')[0]}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Submissions">
                        <IconButton size="small" onClick={() => openSubmissions(mod)} sx={{ color: '#1565c0' }}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(mod.id)} sx={{ color: '#c62828' }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        {modules.length > 0 && (
          <TablePagination
            component="div" count={modules.length}
            page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Card>

      {/* ── Create Dialog ── */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 900, color: '#1a237e', pb: 1 }}>
          Create Learning Progress — {selected?.standard} {selected?.section}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" label="Module Title *"
                value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Description"
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" fontWeight={800} color="#1a237e" mb={1.5}>
            Categories & Checklist Items
          </Typography>

          {categories.map((cat, ci) => (
            <Card key={ci} sx={{ mb: 2, borderRadius: 2, border: `2px solid ${cat.color}`, background: cat.color + '30' }}>
              <CardContent sx={{ pb: '12px !important' }}>
                {/* Category header */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                  <Select value={cat.icon} onChange={e => setCatField(ci, 'icon', e.target.value)}
                    size="small" sx={{ width: 65, fontSize: 18 }}>
                    {CAT_ICONS.map(ic => <MenuItem key={ic} value={ic}>{ic}</MenuItem>)}
                  </Select>
                  <TextField size="small" label="Category Title *" sx={{ flex: 1 }}
                    value={cat.title} onChange={e => setCatField(ci, 'title', e.target.value)} />
                  <Select value={cat.color} onChange={e => setCatField(ci, 'color', e.target.value)}
                    size="small" sx={{ width: 52 }}
                    renderValue={v => <Box sx={{ width: 20, height: 20, borderRadius: 1, background: v }} />}>
                    {CAT_COLORS.map(c => (
                      <MenuItem key={c} value={c}>
                        <Box sx={{ width: 24, height: 24, borderRadius: 1, background: c, border: '1px solid #ddd' }} />
                      </MenuItem>
                    ))}
                  </Select>
                  {categories.length > 1 && (
                    <IconButton size="small" onClick={() => removeCategory(ci)} sx={{ color: '#c62828' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {/* Items */}
                {cat.items.map((item, ii) => (
                  <Box key={ii} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <CheckBoxOutlineBlank sx={{ color: '#bbb', flexShrink: 0 }} fontSize="small" />
                    <TextField size="small" fullWidth placeholder={`Item ${ii + 1}`}
                      value={item} onChange={e => setItem(ci, ii, e.target.value)} />
                    {cat.items.length > 1 && (
                      <IconButton size="small" onClick={() => removeItem(ci, ii)}>
                        <Delete fontSize="small" sx={{ color: '#e57373' }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button size="small" startIcon={<Add />} onClick={() => addItem(ci)}
                  sx={{ color: '#1565c0', fontWeight: 700 }}>
                  Add Item
                </Button>
              </CardContent>
            </Card>
          ))}

          <Button variant="outlined" startIcon={<Add />} onClick={addCategory} fullWidth
            sx={{ borderRadius: 2, borderStyle: 'dashed', color: '#1a237e', borderColor: '#1a237e', fontWeight: 700 }}>
            Add Category
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDialog(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.title.trim()}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 120 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Create Module'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Submissions Dialog ── */}
      <Dialog open={subDialog} onClose={() => setSubDialog(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 900, color: '#1a237e' }}>
          Student Submissions — {subModule?.title}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          {subLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress sx={{ color: '#1a237e' }} />
            </Box>
          ) : !subData || subData.submissions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: '#bbb' }}>
              <Typography>No students have submitted yet</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 800 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Roll</TableCell>
                    <TableCell sx={{ fontWeight: 800, minWidth: 180 }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Remark / Note</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Submitted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subData.submissions.map(sub => {
                    const checked = (sub.checked_items || []).length;
                    const total = subData.totalItems;
                    const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
                    const color = pct >= 80 ? '#2e7d32' : pct >= 50 ? '#f57c00' : '#c62828';
                    return (
                      <TableRow key={sub.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, background: '#1a237e', fontSize: 12 }}>
                              {sub.student_name?.[0]}
                            </Avatar>
                            <Typography variant="body2" fontWeight={700}>{sub.student_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{sub.roll_number}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={pct}
                              sx={{
                                flex: 1, height: 8, borderRadius: 4,
                                '& .MuiLinearProgress-bar': { background: color }
                              }} />
                            <Chip label={`${checked}/${total}`} size="small"
                              sx={{ background: color + '22', color, fontWeight: 800, fontSize: 11, minWidth: 52 }} />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary"
                            sx={{ maxWidth: 220, overflow: 'hidden', }}>
                            {sub.remark || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {sub.submitted_at?.split(' ')[0]}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSubDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
